import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  doc,
  getDoc,
  deleteDoc,
  updateDoc
} from "firebase/firestore";

const VIEWS = ["day", "week", "month"];

export default function Planner() {
  const { user } = useAuth();

  const [premium, setPremium] = useState(false);
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [plans, setPlans] = useState([]);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [editingPlanId, setEditingPlanId] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [message, setMessage] = useState("");

  /* ---------------- PREMIUM CHECK ---------------- */
  useEffect(() => {
    if (!user) return;

    getDoc(doc(db, "users", user.uid))
      .then(snap => {
        if (snap.exists()) setPremium(!!snap.data().premium);
      })
      .catch(() => setMessage("Failed to load premium status."));
  }, [user]);

  /* ---------------- DATE HELPERS ---------------- */
  const startOfPeriod = () => {
    const d = new Date(currentDate);
    d.setHours(0, 0, 0, 0);

    if (view === "day") return d;
    if (view === "week") {
      d.setDate(d.getDate() - d.getDay());
      return d;
    }
    return new Date(d.getFullYear(), d.getMonth(), 1);
  };

  const endOfPeriod = () => {
    const d = new Date(startOfPeriod());
    if (view === "day") d.setDate(d.getDate() + 1);
    else if (view === "week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    return d;
  };

  /* ---------------- LOAD PLANS ---------------- */
  useEffect(() => {
    if (!user || !premium) return;

    const q = query(
      collection(db, "planner"),
      where("uid", "==", user.uid)
    );

    return onSnapshot(q, snap => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.date.seconds - b.date.seconds);

      setPlans(docs);
    });
  }, [user, premium]);

  /* ---------------- ADD / UPDATE PLAN ---------------- */
  const savePlan = async () => {
    if (!title.trim()) return;

    if (editingPlanId) {
      await updateDoc(doc(db, "planner", editingPlanId), {
        title: title.trim(),
        description: description.trim()
      });
    } else {
      const normalizedDate = new Date(
        selectedDay.getFullYear(),
        selectedDay.getMonth(),
        selectedDay.getDate(),
        12, 0, 0
      );

      await addDoc(collection(db, "planner"), {
        uid: user.uid,
        title: title.trim(),
        description: description.trim(),
        date: Timestamp.fromDate(normalizedDate),
        createdAt: Timestamp.now()
      });
    }

    setTitle("");
    setDescription("");
    setEditingPlanId(null);
    setExpandedPlanId(null);
  };

  /* ---------------- DELETE PLAN ---------------- */
  const deletePlan = async (id) => {
    await deleteDoc(doc(db, "planner", id));
    setExpandedPlanId(null);
    setEditingPlanId(null);
  };

  /* ---------------- NAV ---------------- */
  const move = (dir) => {
    const d = new Date(currentDate);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
    setSelectedDay(d);
  };

  /* ---------------- GRID ---------------- */
  const buildDays = () => {
    const days = [];
    for (let d = new Date(startOfPeriod()); d < endOfPeriod(); d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  };

  const days = buildDays();

  if (!premium) {
    return (
      <div className="planner-locked">
        <h2>Planner is a Premium feature</h2>
        <p>Upgrade to unlock planning tools.</p>
      </div>
    );
  }

  return (
    <div className="planner-container">
      <h1>Planner</h1>

      <div className="planner-tabs">
        {VIEWS.map(v => (
          <button key={v} className={view === v ? "active" : ""} onClick={() => setView(v)}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>

      <div className="planner-nav">
        <button onClick={() => move(-1)}>‹</button>
        <span>{currentDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
        <button onClick={() => move(1)}>›</button>
      </div>

      <div className="planner-grid month-grid">
        {days.map(day => {
          const dayPlans = plans.filter(p =>
            p.date.toDate().toDateString() === day.toDateString()
          );

          return (
            <div key={day.toISOString()} className="planner-cell" onClick={() => setSelectedDay(day)}>
              <div className="planner-cell-header">
                {day.toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}
              </div>

              {dayPlans.length === 0 && <div className="planner-empty-cell">No plans</div>}

              {dayPlans.map(p => (
                <div
                  key={p.id}
                  className="planner-plan"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedPlanId(expandedPlanId === p.id ? null : p.id);
                  }}
                >
                  <strong>{p.title}</strong>

                  {expandedPlanId === p.id && (
                    <>
                      {p.description && (
                        <div className="planner-plan-desc">{p.description}</div>
                      )}

                      <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPlanId(p.id);
                            setTitle(p.title);
                            setDescription(p.description || "");
                          }}
                        >
                          Edit
                        </button>

                        <button
                          className="planner-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePlan(p.id);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="planner-add">
        <strong>
          {editingPlanId ? "Editing plan" : "Adding plan for"}{" "}
          {selectedDay.toDateString()}
        </strong>

        <input
          placeholder="Plan title"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
        <input
          placeholder="Description (optional)"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <button onClick={savePlan}>
          {editingPlanId ? "Save Changes" : "Add Plan"}
        </button>
      </div>
    </div>
  );
}
